export interface H3SelectionNavigationOptions {
	force?: boolean;
}

interface H3SelectionNavigationRequest {
	h3Ids: string[];
	key: string;
	force: boolean;
}

interface H3SelectionUrlSyncQueueOptions {
	getRouteH3Ids: () => readonly string[] | undefined;
	navigate: (h3Ids: string[]) => Promise<unknown>;
}

export interface H3SelectionUrlSyncQueue {
	request: (
		h3Ids: readonly string[],
		options?: H3SelectionNavigationOptions
	) => void;
	dispose: () => void;
	whenIdle: () => Promise<void>;
}

export function getNormalizedH3IdsKey(h3Ids?: readonly string[]): string {
	return JSON.stringify(
		[...(h3Ids || [])].sort((first, second) => first.localeCompare(second))
	);
}

/**
 * Runs at most one h3Ids navigation at a time and coalesces all newer intents.
 * In particular, a clear requested while a drawing navigation is in flight is
 * guaranteed to execute afterwards using the then-current route as its base.
 */
export function createH3SelectionUrlSyncQueue({
	getRouteH3Ids,
	navigate
}: H3SelectionUrlSyncQueueOptions): H3SelectionUrlSyncQueue {
	let isRunning = false;
	let isDisposed = false;
	let pendingRequest: H3SelectionNavigationRequest | null = null;
	let idleResolvers: Array<() => void> = [];

	function resolveIdleWaiters(): void {
		const resolvers = idleResolvers;
		idleResolvers = [];
		resolvers.forEach(resolve => resolve());
	}

	function notifyIdle(): void {
		if (isRunning || pendingRequest) return;
		resolveIdleWaiters();
	}

	async function drain(): Promise<void> {
		if (isRunning) return;
		isRunning = true;

		try {
			while (!isDisposed && pendingRequest) {
				const request = pendingRequest;
				pendingRequest = null;
				const routeKey = getNormalizedH3IdsKey(getRouteH3Ids());

				if (!request.force && request.key === routeKey) continue;

				try {
					await navigate(request.h3Ids);
				} catch {
					// A newer coalesced intent must still run if this navigation fails.
				}
			}
		} finally {
			isRunning = false;
			if (pendingRequest) void drain();
			else notifyIdle();
		}
	}

	return {
		request(h3Ids, options = {}) {
			if (isDisposed) return;

			const key = getNormalizedH3IdsKey(h3Ids);
			const shouldPreserveForcedRequest =
				pendingRequest?.key === key && pendingRequest.force;

			pendingRequest = {
				h3Ids: [...h3Ids],
				key,
				force: Boolean(options.force || shouldPreserveForcedRequest)
			};
			void drain();
		},
		dispose() {
			isDisposed = true;
			pendingRequest = null;
			resolveIdleWaiters();
		},
		whenIdle() {
			if (isDisposed || (!isRunning && !pendingRequest)) {
				return Promise.resolve();
			}

			return new Promise(resolve => idleResolvers.push(resolve));
		}
	};
}
